import { createTheme } from '@mui/material/styles';

// CarbonCare Brand Colors from legacy system (exact match from old code)
export const carbonCareColors = {
  // Primary brand colors - using exact colors from old mycarboncare
  primary: {
    main: '#90bf45',      // Original CarbonCare green (more muted)
    light: '#d1e0b9',     // Light success background from old code
    dark: '#557010',      // Darker green for hover states
    contrastText: '#ffffff',
  },
  
  // Secondary grey colors
  secondary: {
    main: '#56686C',      // CarbonCare dark grey
    light: '#879192',     // Medium grey
    dark: '#373e3f',      // Darker grey
    contrastText: '#ffffff',
  },
  
  // Grey palette - Enhanced for Mira Pro aesthetic
  grey: {
    50: '#fafbfc',        // Warmer very light background
    100: '#f4f6f8',       // Enhanced light background
    200: '#e8f4f2',       // Green-tinted light with better saturation
    300: '#dde3e8',       // Softer border grey
    400: '#c9d1d6',       // Refined medium light
    500: '#9ca6ad',       // Balanced medium grey
    600: '#6b7885',       // Professional medium dark
    700: '#4a5660',       // Modern dark grey
    800: '#2d3748',       // Rich dark text
    900: '#1a202c',       // Deep professional dark
  },
  
  // Semantic colors
  success: {
    main: '#90bf45',      // Use original CarbonCare green for success
    light: '#d1e0b9',     // Light success background from old code
    dark: '#557010',      // Darker green
    contrastText: '#ffffff',
  },
  
  error: {
    main: '#a22e1b',      // Error red from old code
    light: '#f8d7da',
    dark: '#721c24',
    contrastText: '#ffffff',
  },
  
  warning: {
    main: '#f08e1b',      // Warning orange from old code
    light: '#fff3cd',
    dark: '#856404',
    contrastText: '#212529',
  },
  
  info: {
    main: '#17a2b8',
    light: '#d1ecf1',
    dark: '#117a8b',
    contrastText: '#ffffff',
  },
  
  // Background colors - Enhanced with Mira Pro aesthetics
  background: {
    default: '#fafbfc',   // Warmer light background
    paper: '#ffffff',     // Pure white paper
    elevated: 'rgba(255, 255, 255, 0.8)', // Glassmorphism elevated surface
    gradient: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 100%)', // Subtle gradient
  },
  
  // Text colors
  text: {
    primary: '#333333',   // Primary text
    secondary: '#5c6263', // Secondary text
    disabled: '#c3c3c3',  // Disabled text
  },
};

// Professional typography using Inter as primary, with fallbacks
const typography = {
  fontFamily: [
    '"Inter"',
    '"SF Pro Display"',
    '"Open Sans"',
    '"Segoe UI"',
    'system-ui',
    '-apple-system',
    'BlinkMacSystemFont',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  
  // Heading styles - optimized for Inter font
  h1: {
    fontWeight: 800,
    fontSize: '2.75rem',
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2.125rem',
    lineHeight: 1.2,
    letterSpacing: '-0.015em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  h3: {
    fontWeight: 700,
    fontSize: '1.75rem',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.35,
    letterSpacing: '-0.005em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.4,
    letterSpacing: '-0.003em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  h6: {
    fontWeight: 600,
    fontSize: '1.125rem',
    lineHeight: 1.45,
    letterSpacing: '0em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  
  // Body text - optimized for readability
  body1: {
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.7,
    letterSpacing: '-0.003em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  body2: {
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    letterSpacing: '-0.002em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  
  // UI text - clean and modern
  button: {
    fontWeight: 500,
    fontSize: '0.9rem',
    lineHeight: 1.75,
    letterSpacing: '0.01em',
    textTransform: 'none' as const,
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  caption: {
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.66,
    letterSpacing: '0.01em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  overline: {
    fontWeight: 600,
    fontSize: '0.6875rem',
    lineHeight: 2.5,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  
  // Additional subtitle styles
  subtitle1: {
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1.75,
    letterSpacing: '-0.002em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.57,
    letterSpacing: '0em',
    fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
  },
};

// Create the CarbonCare theme
export const carbonCareTheme = createTheme({
  palette: {
    mode: 'light',
    primary: carbonCareColors.primary,
    secondary: carbonCareColors.secondary,
    grey: carbonCareColors.grey,
    success: carbonCareColors.success,
    error: carbonCareColors.error,
    warning: carbonCareColors.warning,
    info: carbonCareColors.info,
    background: carbonCareColors.background,
    text: carbonCareColors.text,
  },
  
  typography,
  
  // Custom spacing (8px base unit)
  spacing: 8,
  
  // Shape configuration
  shape: {
    borderRadius: 8,
  },
  
  // Enhanced Shadow configuration - Mira Pro style
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.04), 0px 1px 4px rgba(0, 0, 0, 0.08)',
    '0px 2px 4px rgba(0, 0, 0, 0.06), 0px 4px 8px rgba(0, 0, 0, 0.10)',
    '0px 4px 6px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.12)',
    '0px 6px 12px rgba(0, 0, 0, 0.06), 0px 12px 24px rgba(0, 0, 0, 0.14)',
    '0px 8px 16px rgba(0, 0, 0, 0.08), 0px 16px 32px rgba(0, 0, 0, 0.16)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
  
  // Component customizations
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@import': [
          'url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap")',
          'url("https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap")'
        ].join('\n'),
        body: {
          background: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 50%, #e8f4f2 100%)',
          minHeight: '100vh',
          fontFamily: typography.fontFamily,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '#root': {
          minHeight: '100vh',
          background: 'transparent',
        },
      },
    },
    
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.04), 0px 1px 4px rgba(0, 0, 0, 0.08)',
          color: carbonCareColors.text.primary,
        },
      },
    },
    
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9rem',
          padding: '10px 20px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
          letterSpacing: '0.01em',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '0.95rem',
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.85rem',
        },
        contained: {
          backgroundColor: carbonCareColors.primary.main,
          color: 'white',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: carbonCareColors.primary.dark,
            boxShadow: '0 4px 12px rgba(144, 191, 69, 0.25)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            backgroundColor: carbonCareColors.grey[300],
            color: carbonCareColors.grey[500],
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: carbonCareColors.grey[300],
          color: carbonCareColors.grey[700],
          backgroundColor: 'transparent',
          '&:hover': {
            borderWidth: '1.5px',
            borderColor: carbonCareColors.primary.main,
            backgroundColor: `${carbonCareColors.primary.main}08`,
            color: carbonCareColors.primary.main,
          },
          '&.MuiButton-outlinedPrimary': {
            borderColor: carbonCareColors.primary.main,
            color: carbonCareColors.primary.main,
            '&:hover': {
              backgroundColor: `${carbonCareColors.primary.main}10`,
              borderColor: carbonCareColors.primary.dark,
            },
          },
        },
        text: {
          color: carbonCareColors.grey[700],
          padding: '10px 16px',
          '&:hover': {
            backgroundColor: carbonCareColors.grey[100],
            color: carbonCareColors.primary.main,
          },
        },
        startIcon: {
          marginRight: '8px',
          marginLeft: '-4px',
        },
        endIcon: {
          marginLeft: '8px',
          marginRight: '-4px',
        },
      },
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #90bf45, #557010)',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.1), 0px 24px 48px rgba(0, 0, 0, 0.08)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(144, 191, 69, 0.2)',
            '&::before': {
              opacity: 1,
            },
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          background: 'linear-gradient(180deg, rgba(144, 191, 69, 0.02), transparent)',
        },
        title: {
          fontSize: '1.125rem',
          fontWeight: 600,
          color: carbonCareColors.text.primary,
          letterSpacing: '-0.01em',
        },
        subheader: {
          fontSize: '0.875rem',
          color: carbonCareColors.text.secondary,
          marginTop: '4px',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '0px',
          },
        },
      },
    },

    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          background: 'rgba(0, 0, 0, 0.01)',
        },
      },
    },
    
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        colorSuccess: {
          backgroundColor: carbonCareColors.success.light,
          color: carbonCareColors.success.dark,
        },
        colorError: {
          backgroundColor: carbonCareColors.error.light,
          color: carbonCareColors.error.dark,
        },
      },
    },
    
    MuiTextField: {
      defaultProps: {
        variant: 'outlined' as const,
        size: 'medium' as const,
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
            fontWeight: 500,
            color: carbonCareColors.grey[600],
            '&.Mui-focused': {
              color: carbonCareColors.primary.main,
              fontWeight: 600,
            },
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#ffffff',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            fontSize: '0.95rem',
            '& fieldset': {
              borderColor: carbonCareColors.grey[300],
              borderWidth: '1.5px',
              transition: 'all 0.2s ease-in-out',
            },
            '&:hover': {
              backgroundColor: carbonCareColors.grey[50],
              '& fieldset': {
                borderColor: carbonCareColors.primary.light,
                borderWidth: '1.5px',
              },
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              boxShadow: `0 0 0 3px ${carbonCareColors.primary.main}20`,
              '& fieldset': {
                borderColor: carbonCareColors.primary.main,
                borderWidth: '2px',
              },
            },
            '&.Mui-error': {
              '& fieldset': {
                borderColor: carbonCareColors.error.main,
                borderWidth: '1.5px',
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${carbonCareColors.error.main}20`,
              },
            },
            '& input': {
              padding: '14px 16px',
              fontSize: '0.95rem',
              fontWeight: 400,
              '&::placeholder': {
                color: carbonCareColors.grey[400],
                opacity: 1,
              },
            },
            '& .MuiInputAdornment-root': {
              color: carbonCareColors.grey[500],
            },
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.75rem',
            marginTop: '6px',
            marginLeft: '14px',
            fontWeight: 400,
            '&.Mui-error': {
              fontWeight: 500,
            },
          },
        },
      },
    },
    
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // Both medium and small use padding: '14px 16px' (theme overrides MUI's small default),
          // so both need the same resting transform to keep the label vertically centred with text.
          '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 14px) scale(1)',
          },
          // Small variant intentionally inherits the same rule above — no separate override needed.
        },
      },
    },

    MuiSelect: {
      defaultProps: {
        variant: 'outlined' as const,
        size: 'medium' as const,
      },
      styleOverrides: {
        select: {
          padding: '14px 16px',
          fontSize: '0.95rem',
          fontWeight: 400,
          // Remove minHeight — it inflated Select height above TextField height
          minHeight: 'unset',
          display: 'flex',
          alignItems: 'center',
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
        icon: {
          color: carbonCareColors.grey[600],
          right: 12,
          fontSize: '1.25rem',
        },
      },
    },
    
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            padding: '6px 9px',
            '& .MuiAutocomplete-input': {
              padding: '7.5px 4px',
              fontSize: '0.95rem',
            },
          },
        },
        paper: {
          borderRadius: 10,
          marginTop: 4,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${carbonCareColors.grey[200]}`,
        },
        listbox: {
          padding: '4px',
          '& .MuiAutocomplete-option': {
            borderRadius: 6,
            margin: '2px 4px',
            fontSize: '0.9rem',
            '&:hover': {
              backgroundColor: carbonCareColors.grey[100],
            },
            '&[aria-selected="true"]': {
              backgroundColor: carbonCareColors.primary.light,
              color: carbonCareColors.primary.dark,
              fontWeight: 500,
              '&:hover': {
                backgroundColor: carbonCareColors.primary.light,
              },
            },
          },
        },
      },
    },
    
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '1.25rem',
        },
      },
    },
    
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
          fontWeight: 500,
          color: carbonCareColors.grey[700],
          marginBottom: '6px',
          '&.Mui-focused': {
            color: carbonCareColors.primary.main,
          },
        },
      },
    },
    
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
          fontFamily: '"Inter", "SF Pro Display", "Open Sans", sans-serif',
        },
      },
    },
    
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '8px',
          color: carbonCareColors.grey[400],
          '&.Mui-checked': {
            color: carbonCareColors.primary.main,
          },
          '&:hover': {
            backgroundColor: `${carbonCareColors.primary.main}08`,
          },
        },
      },
    },
    
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '8px',
          color: carbonCareColors.grey[400],
          '&.Mui-checked': {
            color: carbonCareColors.primary.main,
          },
          '&:hover': {
            backgroundColor: `${carbonCareColors.primary.main}08`,
          },
        },
      },
    },
    
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 48,
          height: 28,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: carbonCareColors.primary.main,
                opacity: 1,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: 24,
            height: 24,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
          },
          '& .MuiSwitch-track': {
            borderRadius: 14,
            backgroundColor: carbonCareColors.grey[400],
            opacity: 1,
          },
        },
      },
    },
    
    // Note: MuiDataGrid styling will be applied via sx props directly in components
    
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          padding: 0,
        },
      },
    },
    
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: carbonCareColors.grey[50],
          borderBottom: `1px solid ${carbonCareColors.grey[200]}`,
          fontWeight: 600,
        },
      },
    },
    
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: carbonCareColors.primary.main,
          height: 3,
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 48,
          '&.Mui-selected': {
            color: carbonCareColors.primary.main,
          },
        },
      },
    },
    
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            borderRadius: 12,
            fontWeight: 500,
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(10px)',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.04), 0px 4px 8px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.06), 0px 12px 24px rgba(0, 0, 0, 0.10)',
        },
      },
    },

    MuiStepper: {
      styleOverrides: {
        root: {
          padding: '24px',
          background: 'transparent',
        },
      },
    },

    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
    },

    MuiStep: {
      styleOverrides: {
        root: {
          padding: '0',
        },
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        root: {
          cursor: 'default',
        },
        label: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: carbonCareColors.grey[600],
          '&.Mui-active': {
            color: carbonCareColors.primary.main,
            fontWeight: 600,
          },
          '&.Mui-completed': {
            color: carbonCareColors.primary.main,
            fontWeight: 600,
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: '1.75rem',
          color: carbonCareColors.grey[300],
          '&.Mui-active': {
            color: carbonCareColors.primary.main,
            transform: 'scale(1.1)',
            transition: 'transform 0.2s ease-in-out',
          },
          '&.Mui-completed': {
            color: carbonCareColors.primary.main,
          },
        },
        text: {
          fill: '#ffffff',
          fontSize: '0.75rem',
          fontWeight: 600,
        },
      },
    },

    MuiStepConnector: {
      styleOverrides: {
        root: {
          top: 12,
          "&.Mui-active": {
            "&.MuiStepConnector-line": {
              "borderColor": "#90bf45",
              "borderWidth": 2
            }
          },
          "&.Mui-completed": {
            "&.MuiStepConnector-line": {
              "borderColor": "#90bf45",
              "borderWidth": 2
            }
          }
        },
        line: {
          borderColor: carbonCareColors.grey[300],
          borderTopWidth: 2,
          borderRadius: 1,
        },
        lineHorizontal: {
          borderTopWidth: 2,
        },
        // active: {
        //   '&.MuiStepConnector-line': {
        //     borderColor: carbonCareColors.primary.main,
        //     borderWidth: 2,
        //   },
        // },
        // completed: {
        //   '&.MuiStepConnector-line': {
        //     borderColor: carbonCareColors.primary.main,
        //     borderWidth: 2,
        //   },
        // },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          backgroundColor: carbonCareColors.grey[200],
        },
        bar: {
          borderRadius: 4,
          background: `linear-gradient(90deg, ${carbonCareColors.primary.main}, ${carbonCareColors.primary.dark})`,
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: carbonCareColors.primary.main,
        },
        circle: {
          strokeLinecap: 'round',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 16px',
          fontSize: '0.9rem',
          fontWeight: 500,
          alignItems: 'center',
          border: '1px solid',
        },
        standardSuccess: {
          backgroundColor: carbonCareColors.success.light,
          color: carbonCareColors.success.dark,
          borderColor: carbonCareColors.success.main,
          '& .MuiAlert-icon': {
            color: carbonCareColors.success.main,
          },
        },
        standardError: {
          backgroundColor: carbonCareColors.error.light,
          color: carbonCareColors.error.dark,
          borderColor: carbonCareColors.error.main,
          '& .MuiAlert-icon': {
            color: carbonCareColors.error.main,
          },
        },
        standardWarning: {
          backgroundColor: carbonCareColors.warning.light,
          color: carbonCareColors.warning.dark,
          borderColor: carbonCareColors.warning.main,
          '& .MuiAlert-icon': {
            color: carbonCareColors.warning.main,
          },
        },
        standardInfo: {
          backgroundColor: carbonCareColors.info.light,
          color: carbonCareColors.info.dark,
          borderColor: carbonCareColors.info.main,
          '& .MuiAlert-icon': {
            color: carbonCareColors.info.main,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: carbonCareColors.grey[800],
          fontSize: '0.8rem',
          fontWeight: 500,
          padding: '8px 12px',
          borderRadius: 8,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
        },
        arrow: {
          color: carbonCareColors.grey[800],
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: carbonCareColors.grey[200],
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
          fontSize: '0.7rem',
          minWidth: 20,
          height: 20,
          borderRadius: 10,
        },
        colorPrimary: {
          backgroundColor: carbonCareColors.primary.main,
        },
        colorError: {
          backgroundColor: carbonCareColors.error.main,
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.04), 0px 4px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },

    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: 0,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(180deg, rgba(144, 191, 69, 0.08), rgba(144, 191, 69, 0.04))',
          '& .MuiTableCell-head': {
            fontSize: '0.8rem',
            fontWeight: 700,
            color: carbonCareColors.grey[800],
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${carbonCareColors.primary.main}`,
            padding: '16px',
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'background-color 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(144, 191, 69, 0.04)',
            },
            '&:nth-of-type(even)': {
              backgroundColor: 'rgba(0, 0, 0, 0.01)',
            },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '16px',
          borderBottom: `1px solid ${carbonCareColors.grey[200]}`,
          color: carbonCareColors.text.primary,
        },
        body: {
          color: carbonCareColors.text.primary,
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${carbonCareColors.grey[200]}`,
          '& .MuiTablePagination-toolbar': {
            padding: '16px',
          },
        },
        selectLabel: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        displayedRows: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        select: {
          paddingRight: '36px !important',
        },
      },
    },
  },
});

export default carbonCareTheme;